/**
 * Utilitaires de formatage pour les données envoyées à l'API Cofidis
 */

/**
 * Supprime les accents d'une chaîne de caractères
 */
function removeAccents(str: string): string {
  return str
    .replace(/[Œœ]/g, 'OE')
    .replace(/[Ææ]/g, 'AE')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Formate un nom de ville selon les exigences de l'API Cofidis
 * - MAJUSCULES uniquement
 * - Format ALPHA_NUM (pas de caractères spéciaux)
 * - Arrondissements formatés : "PARIS 01", "LYON 07", etc.
 *
 * Exemples:
 * - "Paris 1er Arrondissement" → "PARIS 01"
 * - "Lyon 7e Arrondissement" → "LYON 07"
 * - "Marseille 15e Arrondissement" → "MARSEILLE 15"
 * - "Saint-Étienne" → "SAINT ETIENNE"
 * - "L'Haÿ-les-Roses" → "L HAY LES ROSES"
 * - "Nice" → "NICE"
 */
export function formatCityForApi(city: string): string {
  if (!city || typeof city !== 'string') {
    return '';
  }

  let formatted = city.trim();

  // Détecter et formater les arrondissements
  // Patterns: "Paris 1er", "Lyon 7e", "Marseille 15ème", etc.
  const arrondissementPattern = /^(.+?)\s+(\d{1,2})(?:er|e|ème|eme)\s*(?:arrondissement)?$/i;
  const match = formatted.match(arrondissementPattern);

  if (match) {
    const cityName = match[1]; // Ex: "Paris", "Lyon"
    const districtNumber = match[2].padStart(2, '0'); // Ex: "01", "07", "15"

    // Formater le nom de la ville
    let formattedCityName = cityName.toUpperCase();
    formattedCityName = removeAccents(formattedCityName);

    // Remplacer les caractères spéciaux par des espaces
    formattedCityName = formattedCityName
      .replace(/['\-]/g, ' ')  // Remplacer apostrophes et tirets par espaces
      .replace(/[^A-Z0-9\s]/g, '')  // Supprimer tous les autres caractères spéciaux
      .replace(/\s+/g, ' ')  // Normaliser les espaces multiples
      .trim();

    return `${formattedCityName} ${districtNumber}`;
  }

  // Pas d'arrondissement détecté, formater normalement
  formatted = formatted.toUpperCase();
  formatted = removeAccents(formatted);

  // Remplacer les caractères spéciaux par des espaces
  formatted = formatted
    .replace(/['\-]/g, ' ')  // Remplacer apostrophes et tirets par espaces
    .replace(/[^A-Z0-9\s]/g, '')  // Supprimer tous les autres caractères spéciaux
    .replace(/\s+/g, ' ')  // Normaliser les espaces multiples
    .trim();

  // S'assurer que la ville n'est pas vide
  if (!formatted || formatted.length === 0) {
    return city.slice(0, 50).toUpperCase();  // Fallback: juste uppercaser si tout est supprimé
  }

  return formatted;
}

/**
 * Formate un nom de personne pour l'API (MAJUSCULES, sans accents)
 */
export function formatNameForApi(name: string): string {
  if (!name || typeof name !== 'string') {
    return '';
  }

  let formatted = name.trim().toUpperCase();
  formatted = removeAccents(formatted);

  // Remplacer les caractères spéciaux par des espaces
  formatted = formatted
    .replace(/['\-]/g, ' ')
    .replace(/[^A-Z\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return formatted;
}

/**
 * Formate un nom d'entreprise pour l'API Cofidis
 * - Format ALPHA_NUM : lettres, chiffres, espaces uniquement
 * - & → ET, autres caractères spéciaux → espace
 * - Majuscules, sans accents, espaces normalisés
 * - Tronqué à 64 caractères (limite spec)
 */
export function formatCompanyNameForApi(name: string): string {
  if (!name || typeof name !== 'string') {
    return '';
  }

  let formatted = name.trim().toUpperCase();
  formatted = removeAccents(formatted);

  // Remplacer & par ET avant de supprimer les caractères spéciaux
  formatted = formatted.replace(/&/g, ' ET ');

  // Supprimer tous les caractères non-alphanumériques et non-espaces
  formatted = formatted
    .replace(/[^A-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Limiter à 64 caractères (limite spec Cofidis)
  if (formatted.length > 64) {
    formatted = formatted.slice(0, 64).trim();
  }

  if (!formatted || formatted.length === 0) {
    return name.slice(0, 64).toUpperCase();
  }

  return formatted;
}

/**
 * Formate une adresse pour l'API (majuscules, sans caractères spéciaux excessifs)
 */
export function formatAddressForApi(address: string): string {
  if (!address || typeof address !== 'string') {
    return '';
  }

  let formatted = address.trim().toUpperCase();
  formatted = removeAccents(formatted);

  // Garder les chiffres, lettres, espaces et quelques caractères communs
  formatted = formatted
    .replace(/['\-]/g, ' ')
    .replace(/[^A-Z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Limiter à 38 caractères (limite API)
  if (formatted.length > 38) {
    formatted = formatted.slice(0, 38);
  }

  // S'assurer que l'adresse n'est pas vide
  if (!formatted || formatted.length === 0) {
    return address.slice(0, 38).toUpperCase();  // Fallback
  }

  return formatted;
}

